import { useCallback } from 'react';
import { IconCheck, IconLoader2, IconAlertCircle } from '@tabler/icons-react';
import { useMedicalRecords } from '../hooks/useMedicalRecords';
import { useSectionCompletion } from '../hooks/useSectionCompletion';
import { showSuccessToast } from '../utils/notifications';
import { RecordCard } from '../components/medicalRecords/RecordCard';
import { RecordDropzone } from '../components/medicalRecords/RecordDropzone';

export function MedicalRecords() {
  const { returnToDashboard } = useSectionCompletion('documents');
  const {
    medicalRecordsData,
    isLoading,
    isSaving,
    error,
    hasRecords,
    reviewedFlags,
    setReviewedFlag,
    uploadFiles,
    deleteRecord,
  } = useMedicalRecords();

  // Handle file upload
  const handleUpload = useCallback(
    async (files: File[]): Promise<boolean> => {
      return uploadFiles(files);
    },
    [uploadFiles]
  );

  // Handle record deletion
  const handleDelete = useCallback(
    async (fhirId: string) => {
      await deleteRecord(fhirId);
    },
    [deleteRecord]
  );

  // Toggle "no records" flag
  const handleToggleNoRecords = useCallback(() => {
    setReviewedFlag('noRecords', !reviewedFlags.noRecords);
  }, [reviewedFlags.noRecords, setReviewedFlag]);

  // Save and return to dashboard
  const handleSave = () => {
    showSuccessToast('Medical records saved');
    returnToDashboard();
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <IconLoader2 className="animate-spin text-cyan-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Medical Records</h2>
        <p className="text-gray-400">
          Upload your medical records, lab results, imaging reports, and clinical documents.
          We'll organize them for you automatically.
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3 text-red-400">
          <IconAlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Upload Section */}
      <section>
        <RecordDropzone onUpload={handleUpload} disabled={isSaving || reviewedFlags.noRecords} />
      </section>

      {/* Uploaded Records */}
      {medicalRecordsData.records.length > 0 && (
        <section>
          <h3 className="text-lg font-semibold text-gray-200 mb-4">
            Uploaded Records ({medicalRecordsData.records.length})
          </h3>
          <div className="space-y-3">
            {medicalRecordsData.records.map((record) => (
              <RecordCard
                key={record.localId}
                record={record}
                onDelete={() => record.fhirId && handleDelete(record.fhirId)}
                disabled={isSaving}
              />
            ))}
          </div>
        </section>
      )}

      {/* Empty state */}
      {medicalRecordsData.records.length === 0 && !hasRecords && (
        <div className="p-6 border border-dashed border-gray-700 rounded-lg text-center text-gray-500">
          <p className="mb-2">No records uploaded yet.</p>
          <p className="text-sm">
            Upload lab results, imaging reports, clinical notes, or any other medical documents
            you'd like to share with your care team.
          </p>
        </div>
      )}

      {/* No records to upload option */}
      {!hasRecords && (
        <div className="flex justify-start">
          <button
            type="button"
            onClick={handleToggleNoRecords}
            disabled={isSaving}
            className={`px-4 py-3 rounded-lg transition-colors flex items-center gap-2 ${reviewedFlags.noRecords
              ? 'bg-green-500/20 border border-green-500/50 text-green-400'
              : 'border border-dashed border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600'
              }`}
          >
            <IconCheck size={16} />
            No records to upload at this time
          </button>
        </div>
      )}

      {/* Action Button */}
      <div className="flex justify-end pt-6 border-t border-gray-800">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="btn disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isSaving && <IconLoader2 className="animate-spin" size={16} />}
          Save
        </button>
      </div>
    </div>
  );
}
